import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ChevronLeft, ChevronRight, ArrowRight, Building2, Quote, X } from "lucide-react";

function ytId(url) {
  const m = (url || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : "";
}

function VideoModal({ t, onClose }) {
  const id = ytId(t?.youtube_url);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  if (!t) return null;
  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-sm" onClick={onClose} data-testid="video-modal-backdrop" />
      <div className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(920px,94vw)] rounded-3xl bg-white p-3 shadow-2xl" role="dialog" aria-modal="true" aria-label={`${t.name} video testimonial`} data-testid="video-modal">
        <button onClick={onClose} aria-label="Close video" className="absolute -right-3 -top-3 z-10 w-10 h-10 rounded-full bg-[#FFC72C] text-[#17263B] flex items-center justify-center shadow-lg" data-testid="video-modal-close"><X className="w-5 h-5" /></button>
        {id ? (
          <iframe key={id} className="w-full aspect-video rounded-2xl bg-black" src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
            title={`${t.name} testimonial`} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid="video-modal-iframe" />
        ) : (
          <div className="aspect-video rounded-2xl bg-slate-100 flex items-center justify-center text-slate-500">Video unavailable</div>
        )}
        <div className="px-3 py-3">
          <p className="m-0 font-extrabold text-[#17263B]" data-testid="video-modal-name">{t.name}</p>
          <p className="m-0 text-sm text-slate-500">{[t.role, t.company, t.program_completed].filter(Boolean).join(" · ")}</p>
        </div>
      </div>
    </>
  );
}

function FeaturedPlayer({ t, playing, onPlay }) {
  const id = ytId(t.youtube_url);
  return (
    <div className="relative rounded-3xl overflow-hidden bg-slate-900 aspect-[16/10] sm:aspect-[16/9] shadow-xl" data-testid="lt-featured">
      {playing && id ? (
        <iframe key={id} className="absolute inset-0 w-full h-full" src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={`${t.name} testimonial`} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid="lt-featured-iframe" />
      ) : (
        <>
          {t.cover_image && <img src={t.cover_image} alt={`${t.name} cover`} className="absolute inset-0 w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          {id && (
            <button onClick={onPlay} aria-label={`Play ${t.name}'s story`}
              className="absolute inset-0 m-auto w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white text-[#17263B] flex items-center justify-center shadow-2xl hover:scale-105 transition-transform"
              data-testid="lt-featured-play">
              <Play className="w-7 h-7 ml-1 fill-current" />
            </button>
          )}
          <div className="absolute left-0 right-0 bottom-0 p-5 sm:p-7 text-left">
            {t.role && <span className="inline-block rounded-full bg-[#FFC72C] text-[#17263B] text-xs font-extrabold px-3.5 py-1.5" data-testid="lt-featured-badge">Placed as {t.role}</span>}
            <h3 className="text-white text-xl sm:text-2xl font-extrabold tracking-tight mt-2.5" data-testid="lt-featured-name">{t.name}</h3>
            {(t.program || t.program_completed) && <p className="text-slate-200 text-sm mt-0.5">{t.program || t.program_completed}</p>}
            {t.company && <p className="flex items-center gap-1.5 text-slate-300 text-xs mt-1.5"><Building2 className="w-3.5 h-3.5" /> {t.company}</p>}
          </div>
        </>
      )}
    </div>
  );
}

function VideoCard({ t, i, onSelect, compact = false }) {
  const id = ytId(t.youtube_url);
  return (
    <button onClick={onSelect} className={`group shrink-0 ${compact ? "w-64 sm:w-72" : "w-60 sm:w-64"} text-left snap-start`} data-testid={`lt-card-${i}`}>
      <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 aspect-[16/10] group-hover:border-amber-300 group-hover:shadow-lg transition-all">
        {t.cover_image && <img src={t.cover_image} alt={t.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />}
        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors" />
        {id && (
          <span className="absolute inset-0 m-auto w-11 h-11 rounded-full bg-white/95 text-[#17263B] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <Play className="w-5 h-5 ml-0.5 fill-current" />
          </span>
        )}
        {t.duration && <span className="absolute bottom-2.5 right-2.5 rounded-md bg-black/75 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid={`lt-duration-${i}`}>{t.duration}</span>}
      </div>
      {compact ? (
        <>
          {t.role && <p className="font-extrabold text-slate-900 text-sm mt-3 leading-snug" data-testid={`lt-title-${i}`}>Placed as {t.role}{t.company ? ` at ${t.company}` : ""}</p>}
          <p className="font-extrabold text-slate-900 text-sm mt-2" data-testid={`lt-name-${i}`}>{t.name}</p>
          {(t.program || t.program_completed) && <p className="text-xs text-slate-500 mt-0.5">{t.program || t.program_completed}</p>}
        </>
      ) : (
        <>
          <p className="font-extrabold text-slate-900 text-sm mt-3" data-testid={`lt-name-${i}`}>{t.name}</p>
          {(t.program || t.program_completed) && <p className="text-xs text-slate-500 mt-0.5">{t.program || t.program_completed}</p>}
          {t.company && <p className="flex items-center gap-1.5 text-xs text-slate-500 mt-1"><Building2 className="w-3.5 h-3.5 text-slate-400" /> {t.company}</p>}
          {t.quote && <p className="flex gap-1.5 text-xs text-slate-500 leading-relaxed mt-2 line-clamp-3"><Quote className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" /> {t.quote}</p>}
        </>
      )}
    </button>
  );
}

export default function TestimonialVideos({ cms, layout = "featured", header = null, moreLink = null }) {
  const all = (cms?.testimonials || []).filter((t) => t.visible !== false && t.name);
  const items = [...all].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const [featured, setFeatured] = useState(() => items.find((t) => t.featured) || items[0] || null);
  const [playing, setPlaying] = useState(false);
  const [active, setActive] = useState(null);
  const railRef = useRef(null);

  if (items.length === 0) return null;
  const current = (featured && items.includes(featured)) ? featured : items[0];
  const avatars = items.filter((t) => t.photo).slice(0, 4);
  const scroll = (dir) => railRef.current?.scrollBy({ left: dir * 300, behavior: "smooth" });
  const select = (t) => { setFeatured(t); setPlaying(false); };

  if (layout === "rail") {
    return (
      <section className="relative bg-white overflow-hidden" data-testid="learner-testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18 relative">
          <div className="flex items-end justify-between gap-5">
            <div className="min-w-0">{header}</div>
            {items.length > 2 && (
              <div className="hidden sm:flex items-center gap-2.5 shrink-0">
                <button onClick={() => scroll(-1)} aria-label="Previous testimonials" className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:border-amber-300 hover:text-slate-800 transition-colors" data-testid="lt-prev"><ChevronLeft className="w-5 h-5" /></button>
                <button onClick={() => scroll(1)} aria-label="Next testimonials" className="w-10 h-10 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] flex items-center justify-center text-[#17263B] transition-colors" data-testid="lt-next"><ChevronRight className="w-5 h-5" /></button>
              </div>
            )}
          </div>
          <div ref={railRef} className="mt-9 flex gap-5 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="lt-rail">
            {items.map((t, i) => <VideoCard key={t.name + i} t={t} i={i} compact onSelect={() => setActive(t)} />)}
          </div>
          {moreLink?.href && (
            <a href={moreLink.href} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm font-extrabold text-amber-700 hover:text-amber-800" data-testid="lt-watch-more">
              {moreLink.text} <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>
        {active && <VideoModal t={active} onClose={() => setActive(null)} />}
      </section>
    );
  }

  return (
    <section className="relative bg-white overflow-hidden" data-testid="learner-testimonials">
      <div className="absolute -left-24 top-16 w-72 h-72 rounded-full bg-amber-100/50 blur-3xl" aria-hidden="true" />
      <div className="absolute right-8 top-10 w-24 h-24 opacity-60 hidden lg:block" style={{ backgroundImage: "radial-gradient(#F5B301 1.6px, transparent 1.6px)", backgroundSize: "16px 16px" }} aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative">
        <div className="grid lg:grid-cols-[42%_58%] gap-10 lg:gap-12 items-center">
          <div>
            <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.2em] text-slate-500" data-testid="lt-eyebrow">
              <span className="h-0.5 w-8 bg-[#F5B301]" /> Learner Testimonials
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#17263B] mt-4 leading-tight" data-testid="lt-title">
              Hear from our{" "}
              <span className="relative inline-block text-[#F5B301] italic">learners
                <svg className="absolute left-0 -bottom-1.5 w-full" viewBox="0 0 120 10" preserveAspectRatio="none" aria-hidden="true"><path d="M2 7 Q 60 1 118 6" stroke="#F5B301" strokeWidth="3.5" strokeLinecap="round" fill="none" /></svg>
              </span>
            </h2>
            <p className="text-base sm:text-lg font-semibold text-slate-700 mt-4">Real learners. Real skills. Real career journeys.</p>
            <p className="text-sm sm:text-base text-slate-500 mt-2 leading-relaxed">From building in-demand skills to landing dream jobs, our learners share how Training Basket has helped them grow in their careers.</p>
            <div className="flex flex-wrap items-center gap-6 mt-7">
              <Link to="/success-stories" data-testid="lt-view-all">
                <span className="inline-flex items-center gap-2 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] font-bold px-7 py-3.5 text-sm transition-all hover:-translate-y-0.5 shadow-sm">
                  View All Success Stories <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2.5">
                  {avatars.map((a, i) => <img key={i} src={a.photo} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-white shadow" loading="lazy" />)}
                </div>
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight" data-testid="lt-learner-count">5000+</p>
                  <p className="text-xs text-slate-500">Successful Learners</p>
                </div>
              </div>
            </div>
          </div>
          <FeaturedPlayer t={current} playing={playing} onPlay={() => setPlaying(true)} />
        </div>

        <div className="relative mt-12">
          <div ref={railRef} className="flex gap-5 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="lt-rail">
            {items.map((t, i) => <VideoCard key={t.name + i} t={t} i={i} onSelect={() => select(t)} />)}
          </div>
          {items.length > 3 && (
            <div className="hidden sm:flex absolute -top-1 -translate-y-full right-0 items-center gap-2.5 max-lg:hidden">
              <button onClick={() => scroll(-1)} aria-label="Previous testimonials" className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:border-amber-300 hover:text-slate-800 transition-colors" data-testid="lt-prev"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={() => scroll(1)} aria-label="Next testimonials" className="w-10 h-10 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] flex items-center justify-center text-[#17263B] transition-colors" data-testid="lt-next"><ChevronRight className="w-5 h-5" /></button>
            </div>
          )}
          {items.length > 3 && (
            <div className="hidden lg:flex absolute top-16 -translate-y-1/2 -left-5 -right-5 justify-between pointer-events-none">
              <button onClick={() => scroll(-1)} aria-label="Previous testimonials" className="pointer-events-auto w-10 h-10 rounded-full border border-slate-200 bg-white shadow flex items-center justify-center text-slate-500 hover:border-amber-300 transition-colors" data-testid="lt-prev-side"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={() => scroll(1)} aria-label="Next testimonials" className="pointer-events-auto w-10 h-10 rounded-full border border-slate-200 bg-white shadow flex items-center justify-center text-slate-500 hover:border-amber-300 transition-colors" data-testid="lt-next-side"><ChevronRight className="w-5 h-5" /></button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
